<ng-container *transloco="let t">
  @if (isCiamEnabled()) {
    <div
      id="identity-verification-card"
      class="identity-verification-card--ciam flex flex-col w-full max-w-[480px] max-sm:max-w-full min-w-0"
    >
      <div
        class="bg-white px-14 pt-14 pb-3 max-sm:px-4 max-sm:pt-10 flex flex-col gap-6"
        id="identity-verification-inner"
      >
        <div id="identity-verification-header" class="flex flex-col items-start gap-6">
          <img id="identity-verification-logo" [src]="logoUrl()" [alt]="partnerDisplayName()" class="h-9 w-auto" />
          <div class="flex flex-col gap-4">
            @if (userEmail()) {
              <span id="identity-verification-email" class="identity-verification-card__text">
                {{ userEmail() }}
              </span>
            }
            <h1 id="identity-verification-title" class="identity-verification-card__heading m-0">
              {{ t('authIdentityVerificationChooseMethod') }}
            </h1>
          </div>
        </div>

        @if (externalErrorMessage()) {
          <div
            id="identity-verification-error-message"
            class="bg-ld-error-light border border-ld-error rounded-lg px-4 py-3 text-ld-error text-sm"
            role="alert"
          >
            {{ externalErrorMessage() }}
          </div>
        }

        <p id="identity-verification-description" class="identity-verification-card__text m-0">
          {{ t('authIdentityVerificationDescription') }}
        </p>

        @if (hasOptions()) {
          <div id="identity-verification-form" class="flex flex-col gap-6">
            @if (hasPhones()) {
              <div class="identity-verification-card__select-field">
                <select
                  id="phone-select"
                  class="identity-verification-card__select"
                  [value]="activePhoneFieldName()"
                  (change)="onPhoneSelectNative($event)"
                  [attr.aria-label]="t('authIdentityVerificationPhoneLabel')"
                >
                  @for (phone of phones(); track phone.fieldName) {
                    <option id="phone-option-{{ phone.fieldName }}" [value]="phone.fieldName">
                      {{ phone.maskedValue }}
                    </option>
                  }
                </select>
                <span class="identity-verification-card__select-arrow" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="#2B2A35"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>
            }

            @if (hasEmails()) {
              <div class="identity-verification-card__select-field">
                <select
                  id="email-select"
                  class="identity-verification-card__select"
                  [value]="activeEmailFieldName()"
                  (change)="onEmailSelectNative($event)"
                  [attr.aria-label]="t('authIdentityVerificationEmailLabel')"
                >
                  @for (email of emails(); track email.fieldName) {
                    <option id="email-option-{{ email.fieldName }}" [value]="email.fieldName">
                      {{ email.maskedValue }}
                    </option>
                  }
                </select>
                <span class="identity-verification-card__select-arrow" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="#2B2A35"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>
            }
          </div>
        } @else {
          <div id="identity-verification-no-options" class="py-4">
            <p class="identity-verification-card__text m-0">{{ t('authIdentityVerificationNoOptions') }}</p>
          </div>
        }
      </div>

      <div
        class="bg-white px-14 pt-8 pb-14 max-sm:px-4 max-sm:pb-10 flex justify-end"
        id="identity-verification-actions"
      >
        @if (hasOptions()) {
          <button
            type="button"
            id="identity-verification-proceed-button"
            class="identity-verification-card__btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-wait focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
            [disabled]="!canProceed() || isLoading()"
            (click)="onProceed()"
          >
            @if (isLoading()) {
              <mat-spinner
                diameter="20"
                id="identity-verification-spinner"
                class="text-white! [&_circle]:stroke-white!"
              ></mat-spinner>
              <span>{{ t('authIdentityVerificationSettingUp') }}</span>
            } @else {
              <span>{{ t('commonButtonsContinue') }}</span>
            }
          </button>
        } @else {
          <button
            type="button"
            id="identity-verification-back-button"
            class="identity-verification-card__btn text-white border-0 cursor-pointer transition-all hover:brightness-90 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2"
            (click)="onBack()"
          >
            <span>{{ t('authIdentityVerificationBackToLogin') }}</span>
          </button>
        }
      </div>

      @if (!isJvTheme()) {
        <div
          id="identity-verification-nmls"
          class="identity-verification-card__nmls flex items-center justify-center px-2.5 pt-6 pb-8"
        >
          <span class="identity-verification-card__nmls-text">
            {{ nmlsCopyright }}
          </span>
        </div>
      }
    </div>
  } @else {
    <div
      id="identity-verification-card"
      class="bg-white rounded-2xl shadow-card w-full max-w-[480px] min-w-0 p-4 sm:p-10"
    >
      <div id="identity-verification-header" class="flex flex-col items-center gap-2 mb-8">
        <img id="identity-verification-logo" [src]="logoUrl()" [alt]="partnerDisplayName()" class="h-14 w-auto" />
        <h1 id="identity-verification-title" class="text-section-heading text-ld-neutral-700 m-0">
          {{ t('commonMessagesVerifyIdentity') }}
        </h1>
        <p id="identity-verification-description" class="font-dm text-sm text-ld-neutral-500 text-center m-0 mt-1">
          {{ t('authIdentityVerificationDescription') }}
        </p>
      </div>

      @if (externalErrorMessage()) {
        <div
          id="identity-verification-error-message"
          class="bg-ld-error-light border border-ld-error rounded-lg px-4 py-3 mb-6 text-ld-error text-sm"
          role="alert"
        >
          {{ externalErrorMessage() }}
        </div>
      }

      @if (hasOptions()) {
        <div id="identity-verification-form" class="flex flex-col gap-6">
          @if (hasPhones()) {
            <mat-form-field id="phone-select-field" appearance="outline" class="w-full">
              <mat-label>{{ t('authIdentityVerificationPhoneLabel') }}</mat-label>
              <mat-select
                id="phone-select"
                [value]="activePhoneFieldName()"
                (selectionChange)="onPhoneSelect($event.value)"
              >
                @for (phone of phones(); track phone.fieldName) {
                  <mat-option id="phone-option-{{ phone.fieldName }}" [value]="phone.fieldName">
                    {{ phone.maskedValue }}
                    @if (phone.fieldName === defaultPhoneFieldName()) {
                      <span class="text-ld-neutral-500 text-xs ml-1">({{ t('authIdentityVerificationDefault') }})</span>
                    }
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }

          @if (hasEmails()) {
            <mat-form-field id="email-select-field" appearance="outline" class="w-full">
              <mat-label>{{ t('authIdentityVerificationEmailLabel') }}</mat-label>
              <mat-select
                id="email-select"
                [value]="activeEmailFieldName()"
                [placeholder]="t('authIdentityVerificationEmailPlaceholder')"
                (selectionChange)="onEmailSelect($event.value)"
              >
                @for (email of emails(); track email.fieldName) {
                  <mat-option id="email-option-{{ email.fieldName }}" [value]="email.fieldName">
                    {{ email.maskedValue }}
                    @if (email.fieldName === defaultEmailFieldName()) {
                      <span class="text-ld-neutral-500 text-xs ml-1">({{ t('authIdentityVerificationDefault') }})</span>
                    }
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }

          <button
            type="button"
            id="identity-verification-proceed-button"
            class="w-full h-11 bg-ld-primary text-white border-0 font-dm font-semibold text-base tracking-[0.1px] uppercase cursor-pointer rounded-[22px] transition-all hover:bg-ld-primary-hover active:scale-[0.98] disabled:bg-ld-primary disabled:cursor-wait focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 flex items-center justify-center gap-2"
            [disabled]="!canProceed() || isLoading()"
            (click)="onProceed()"
          >
            @if (isLoading()) {
              <mat-spinner
                diameter="20"
                id="identity-verification-spinner"
                class="text-white! [&_circle]:stroke-white!"
              ></mat-spinner>
              <span>{{ t('authIdentityVerificationSettingUp') }}</span>
            } @else {
              <span>{{ t('authIdentityVerificationProceed') }}</span>
            }
          </button>

          <div id="identity-verification-back-container" class="flex items-center justify-center">
            <button
              type="button"
              id="identity-verification-back-link"
              (click)="onBack()"
              class="bg-transparent border-0 text-ld-link cursor-pointer p-0 font-dm text-sm font-medium leading-4 hover:underline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded touch-target"
            >
              {{ t('authIdentityVerificationBackToLogin') }}
            </button>
          </div>
        </div>
      } @else {
        <div id="identity-verification-no-options" class="text-center py-4">
          <p class="font-dm text-sm text-ld-neutral-500 m-0">{{ t('authIdentityVerificationNoOptions') }}</p>
          <button
            type="button"
            id="identity-verification-no-options-back"
            (click)="onBack()"
            class="mt-4 bg-transparent border-0 text-ld-link cursor-pointer p-0 font-dm text-sm font-medium leading-4 hover:underline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded touch-target"
          >
            {{ t('authIdentityVerificationBackToLogin') }}
          </button>
        </div>
      }
    </div>
  }
</ng-container>
